import { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Calendar,
  Plus,
  Play,
  Clock,
  MapPin,
  ArrowLeft,
  RefreshCw,
  Activity,
  Trophy,
  Search,
  GitBranch,
  Sparkles,
  ChevronRight,
  Target,
} from 'lucide-react';
import { toast } from 'sonner';
import { useAutoRefresh } from '../hooks/useAutoRefresh';
import { API } from '../lib/api';
import { cn } from '../lib/utils';
import { ContextualState, PageLoadingState } from '../components/StatePanel';

const statusFilters = ['all', 'live', 'scheduled', 'completed'];

const Matches = () => {
  const { tournamentId } = useParams();
  const { getAuthHeader, isOrganizer } = useAuth();
  const [tournament, setTournament] = useState(null);
  const [teams, setTeams] = useState([]);
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  const [formData, setFormData] = useState({
    team1_id: '',
    team2_id: '',
    match_date: '',
    match_time: '',
    venue: '',
    round_name: '',
  });

  const fetchData = useCallback(
    async (showError = true) => {
      try {
        const [tournamentRes, teamsRes, matchesRes] = await Promise.all([
          axios.get(`${API}/tournaments/${tournamentId}`, getAuthHeader()),
          axios.get(`${API}/tournaments/${tournamentId}/teams`, getAuthHeader()),
          axios.get(`${API}/tournaments/${tournamentId}/matches`, getAuthHeader()),
        ]);
        setTournament(tournamentRes.data);
        setTeams(teamsRes.data);
        setMatches(matchesRes.data);
      } catch (error) {
        if (showError) {
          toast.error('Failed to load matches');
        }
      } finally {
        setLoading(false);
      }
    },
    [tournamentId, getAuthHeader]
  );

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useAutoRefresh(() => fetchData(false));

  const handleCreateMatch = async (e) => {
    e.preventDefault();
    if (!formData.team1_id || !formData.team2_id || !formData.match_date) {
      toast.error('Please fill required fields');
      return;
    }
    if (formData.team1_id === formData.team2_id) {
      toast.error('Please select different teams');
      return;
    }

    try {
      const response = await axios.post(
        `${API}/tournaments/${tournamentId}/matches`,
        formData,
        getAuthHeader()
      );
      setMatches([...matches, response.data]);
      setDialogOpen(false);
      setFormData({
        team1_id: '',
        team2_id: '',
        match_date: '',
        match_time: '',
        venue: '',
        round_name: '',
      });
      toast.success('Match scheduled!');
    } catch (error) {
      toast.error('Failed to create match');
    }
  };

  const handleGenerateSchedule = async () => {
    if (teams.length < 2) {
      toast.error('Need at least 2 teams to generate schedule');
      return;
    }

    if (matches.length > 0) {
      if (!window.confirm('This will delete existing matches. Continue?')) return;
    }

    setGenerating(true);
    try {
      const response = await axios.post(
        `${API}/tournaments/${tournamentId}/generate-schedule`,
        {},
        getAuthHeader()
      );
      setMatches(response.data.matches);
      toast.success(`Generated ${response.data.matches.length} matches!`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate schedule');
    } finally {
      setGenerating(false);
    }
  };

  const groupedMatches = useMemo(
    () => ({
      live: matches.filter((match) => match.status === 'live'),
      scheduled: matches.filter((match) => match.status === 'scheduled'),
      completed: matches.filter((match) => match.status === 'completed'),
    }),
    [matches]
  );

  const filteredMatches = useMemo(() => {
    const normalizedQuery = searchQuery.trim().toLowerCase();

    return matches.filter((match) => {
      const matchesStatus = statusFilter === 'all' || match.status === statusFilter;
      const matchesQuery =
        !normalizedQuery ||
        match.team1?.name?.toLowerCase().includes(normalizedQuery) ||
        match.team2?.name?.toLowerCase().includes(normalizedQuery) ||
        match.round_name?.toLowerCase().includes(normalizedQuery) ||
        match.venue?.toLowerCase().includes(normalizedQuery);

      return matchesStatus && matchesQuery;
    });
  }, [matches, searchQuery, statusFilter]);

  const filteredGroups = useMemo(
    () => ({
      live: filteredMatches.filter((match) => match.status === 'live'),
      scheduled: filteredMatches.filter((match) => match.status === 'scheduled'),
      completed: filteredMatches.filter((match) => match.status === 'completed'),
    }),
    [filteredMatches]
  );

  const summaryCards = useMemo(
    () => [
      {
        label: 'Total Fixtures',
        value: matches.length,
        icon: Calendar,
        tone: 'border-white/10 bg-white/[0.04]',
      },
      {
        label: 'Live Right Now',
        value: groupedMatches.live.length,
        icon: Activity,
        tone: 'border-rose-500/30 bg-rose-500/10 text-rose-100',
      },
      {
        label: 'Upcoming',
        value: groupedMatches.scheduled.length,
        icon: Clock,
        tone: 'border-sky-500/30 bg-sky-500/10 text-sky-100',
      },
      {
        label: 'Completed',
        value: groupedMatches.completed.length,
        icon: Trophy,
        tone: 'border-emerald-500/30 bg-emerald-500/10 text-emerald-100',
      },
    ],
    [matches.length, groupedMatches]
  );

  if (loading) {
    return (
      <Layout>
        <PageLoadingState body="split" />
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6 md:space-y-8">
        <section className="relative overflow-hidden rounded-[32px] border border-white/10 bg-[#08111f] text-white shadow-[0_24px_80px_rgba(3,8,20,0.45)]">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(56,189,248,0.22),transparent_35%),radial-gradient(circle_at_bottom_right,rgba(34,197,94,0.18),transparent_30%)]" />
          <div className="relative p-5 md:p-7">
            <div className="flex flex-col gap-5 xl:flex-row xl:items-end xl:justify-between">
              <div className="space-y-4">
                <Link
                  to={`/tournaments/${tournamentId}`}
                  className="inline-flex items-center gap-2 text-sm text-white/70 transition hover:text-white"
                >
                  <ArrowLeft className="h-4 w-4" />
                  Back to Tournament Hub
                </Link>
                <div className="space-y-3">
                  <Badge className="rounded-full border border-white/15 bg-white/10 px-3 py-1 text-[11px] uppercase tracking-[0.28em] text-white/80">
                    Match Control Board
                  </Badge>
                  <div>
                    <h1 className="text-3xl font-semibold tracking-tight md:text-4xl">
                      {isOrganizer() ? 'Manage the full fixture flow' : 'Follow the tournament fixture board'}
                    </h1>
                    <p className="mt-2 max-w-3xl text-sm text-white/72 md:text-base">
                      {isOrganizer()
                        ? 'Create fixtures, trigger auto-scheduling, and jump directly into live scoring from one command-style schedule view.'
                        : 'See live games, upcoming fixtures, and completed results in one clear match center built for quick follow-through.'}
                    </p>
                  </div>
                </div>
              </div>

              <div className="grid gap-3 sm:grid-cols-2 xl:min-w-[360px] xl:max-w-[420px]">
                <div className="rounded-2xl border border-white/10 bg-white/8 p-4 backdrop-blur">
                  <p className="text-xs uppercase tracking-[0.22em] text-white/60">Tournament</p>
                  <p className="mt-2 text-lg font-semibold">{tournament?.name}</p>
                  <p className="mt-1 text-sm text-white/65">
                    {teams.length} teams configured for scheduling
                  </p>
                </div>
                <div className="rounded-2xl border border-emerald-400/20 bg-emerald-400/10 p-4 backdrop-blur">
                  <p className="text-xs uppercase tracking-[0.22em] text-emerald-100/70">Flow State</p>
                  <p className="mt-2 text-lg font-semibold text-emerald-50">
                    {groupedMatches.live.length > 0 ? 'Live Fixtures Active' : 'Fixture Planning Ready'}
                  </p>
                  <p className="mt-1 text-sm text-emerald-50/75">
                    {groupedMatches.live.length > 0
                      ? `${groupedMatches.live.length} match${groupedMatches.live.length > 1 ? 'es are' : ' is'} currently running`
                      : teams.length >= 2
                        ? 'Enough teams are available to create or generate matches'
                        : 'Add more teams before generating a schedule'}
                  </p>
                </div>
              </div>
            </div>

            {isOrganizer() && (
              <div className="mt-5 flex flex-col gap-3 sm:flex-row">
                <Button
                  variant="outline"
                  onClick={handleGenerateSchedule}
                  disabled={generating || teams.length < 2}
                  className="h-11 border-white/15 bg-white/8 text-white hover:bg-white/12"
                  data-testid="generate-schedule-button"
                >
                  <RefreshCw className={cn('mr-2 h-4 w-4', generating && 'animate-spin')} />
                  Auto Generate Schedule
                </Button>

                <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                  <DialogTrigger asChild>
                    <Button
                      className="h-11 bg-white text-slate-950 hover:bg-white/90"
                      data-testid="add-match-button"
                    >
                      <Plus className="mr-2 h-4 w-4" />
                      Add Match Manually
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="sm:max-w-[560px]">
                    <DialogHeader>
                      <DialogTitle>Schedule Match</DialogTitle>
                      <DialogDescription>
                        Add a fixture to the tournament calendar with teams, timing, venue, and round details.
                      </DialogDescription>
                    </DialogHeader>

                    <form onSubmit={handleCreateMatch} className="mt-4 space-y-5">
                      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                        <div className="space-y-2">
                          <Label>Team 1 *</Label>
                          <Select
                            value={formData.team1_id}
                            onValueChange={(value) => setFormData({ ...formData, team1_id: value })}
                          >
                            <SelectTrigger data-testid="team1-select">
                              <SelectValue placeholder="Select team" />
                            </SelectTrigger>
                            <SelectContent>
                              {teams.map((team) => (
                                <SelectItem key={team.id} value={team.id}>
                                  {team.name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>

                        <div className="space-y-2">
                          <Label>Team 2 *</Label>
                          <Select
                            value={formData.team2_id}
                            onValueChange={(value) => setFormData({ ...formData, team2_id: value })}
                          >
                            <SelectTrigger data-testid="team2-select">
                              <SelectValue placeholder="Select team" />
                            </SelectTrigger>
                            <SelectContent>
                              {teams.map((team) => (
                                <SelectItem key={team.id} value={team.id}>
                                  {team.name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>

                      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                        <div className="space-y-2">
                          <Label htmlFor="match_date">Date *</Label>
                          <Input
                            id="match_date"
                            type="date"
                            value={formData.match_date}
                            onChange={(e) => setFormData({ ...formData, match_date: e.target.value })}
                            data-testid="match-date-input"
                          />
                        </div>

                        <div className="space-y-2">
                          <Label htmlFor="match_time">Time</Label>
                          <Input
                            id="match_time"
                            type="time"
                            value={formData.match_time}
                            onChange={(e) => setFormData({ ...formData, match_time: e.target.value })}
                            data-testid="match-time-input"
                          />
                        </div>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="round_name">Round / Stage</Label>
                        <Input
                          id="round_name"
                          value={formData.round_name}
                          onChange={(e) => setFormData({ ...formData, round_name: e.target.value })}
                          placeholder="League Match 1 / Semi Final"
                          data-testid="round-name-input"
                        />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="venue">Venue</Label>
                        <Input
                          id="venue"
                          value={formData.venue}
                          onChange={(e) => setFormData({ ...formData, venue: e.target.value })}
                          placeholder={tournament?.venue || 'Match venue'}
                          data-testid="match-venue-input"
                        />
                      </div>

                      <div className="flex gap-3 pt-2">
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => setDialogOpen(false)}
                          className="flex-1"
                        >
                          Cancel
                        </Button>
                        <Button type="submit" className="flex-1" data-testid="submit-match-button">
                          Schedule Match
                        </Button>
                      </div>
                    </form>
                  </DialogContent>
                </Dialog>
              </div>
            )}
          </div>
        </section>

        <section className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
          {summaryCards.map((card) => {
            const Icon = card.icon;
            return (
              <Card key={card.label} className={cn('border-white/10 shadow-sm', card.tone)}>
                <CardContent className="flex items-center justify-between p-5">
                  <div>
                    <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground">
                      {card.label}
                    </p>
                    <p className="mt-2 text-3xl font-semibold tracking-tight">{card.value}</p>
                  </div>
                  <div className="rounded-2xl border border-current/10 bg-background/40 p-3">
                    <Icon className="h-5 w-5" />
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </section>

        <section className="grid gap-6 xl:grid-cols-[1.35fr_0.65fr]">
          <div className="space-y-6">
            <Card className="border-white/10 shadow-sm">
              <CardContent className="space-y-4 p-5">
                <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
                  <div>
                    <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground">
                      Match Board
                    </p>
                    <h2 className="mt-1 text-2xl font-semibold tracking-tight">Fixtures by status</h2>
                  </div>

                  <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                    <div className="relative min-w-[220px] flex-1 sm:flex-none">
                      <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                      <Input
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        placeholder="Search team, round, or venue"
                        className="pl-10"
                      />
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {statusFilters.map((filter) => (
                        <Button
                          key={filter}
                          type="button"
                          variant={statusFilter === filter ? 'default' : 'outline'}
                          size="sm"
                          onClick={() => setStatusFilter(filter)}
                          className="capitalize"
                        >
                          {filter}
                        </Button>
                      ))}
                    </div>
                  </div>
                </div>

                {filteredMatches.length === 0 ? (
                  <ContextualState
                    icon={Calendar}
                    eyebrow="Fixture Board"
                    title={matches.length === 0 ? 'No matches scheduled yet' : 'No fixtures match this view'}
                    description={
                      matches.length === 0
                        ? teams.length < 2
                          ? 'Add at least two teams before creating or generating the schedule.'
                          : isOrganizer()
                            ? 'Use auto-generate to build the fixture list quickly, or add individual matches manually.'
                            : 'Fixtures will appear here as soon as the organizer publishes the schedule.'
                        : 'Try a different search or status filter to bring those fixtures back into view.'
                    }
                  />
                ) : (
                  <div className="space-y-6">
                    {filteredGroups.live.length > 0 && (
                      <FixtureLane
                        title="Live Now"
                        subtitle="Matches currently active and ready for real-time monitoring."
                        icon={Activity}
                        tone="live"
                        matches={filteredGroups.live}
                        canEdit={isOrganizer()}
                      />
                    )}

                    {filteredGroups.scheduled.length > 0 && (
                      <FixtureLane
                        title="Upcoming Fixtures"
                        subtitle="Planned matches waiting to be started."
                        icon={Clock}
                        tone="scheduled"
                        matches={filteredGroups.scheduled}
                        canEdit={isOrganizer()}
                      />
                    )}

                    {filteredGroups.completed.length > 0 && (
                      <FixtureLane
                        title="Completed Results"
                        subtitle="Finished matches with final outcome and scorecard access."
                        icon={Trophy}
                        tone="completed"
                        matches={filteredGroups.completed}
                        canEdit={isOrganizer()}
                      />
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          <div className="space-y-6">
            <Card className="border-white/10 bg-[#091222] text-white shadow-sm">
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-lg">
                  <GitBranch className="h-5 w-5 text-cyan-300" />
                  Fixture Engine
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4 text-sm text-white/72">
                <div className="rounded-2xl border border-white/10 bg-white/6 p-4">
                  <p className="text-xs uppercase tracking-[0.18em] text-white/55">Auto Schedule Readiness</p>
                  <p className="mt-2 text-base font-semibold text-white">
                    {teams.length >= 2 ? 'Ready to generate' : 'Waiting on more teams'}
                  </p>
                  <p className="mt-1">
                    {teams.length >= 2
                      ? 'The bracket engine has enough teams to build the next fixture plan.'
                      : `Only ${teams.length} team${teams.length === 1 ? '' : 's'} available. Add more to unlock automatic scheduling.`}
                  </p>
                </div>

                <div className="space-y-3 rounded-2xl border border-white/10 bg-white/6 p-4">
                  <p className="text-xs uppercase tracking-[0.18em] text-white/55">Fixture cues</p>
                  <div className="space-y-3">
                    <InsightRow
                      icon={Sparkles}
                      title="Live management"
                      description="Jump straight from a live card into scoring without switching workflow context."
                    />
                    <InsightRow
                      icon={Target}
                      title="Round awareness"
                      description="Round and venue metadata stay visible on every card so bracket context is never lost."
                    />
                    <InsightRow
                      icon={RefreshCw}
                      title="Fresh data"
                      description="This view refreshes automatically, so viewers see schedule changes without manual reload."
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-white/10 shadow-sm">
              <CardHeader className="pb-3">
                <CardTitle className="text-lg">Quick Links</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <QuickLink
                  to={`/tournaments/${tournamentId}`}
                  title="Tournament hub"
                  description="Return to the central overview for this competition."
                />
                <QuickLink
                  to={`/tournaments/${tournamentId}/points-table`}
                  title="Standings"
                  description="Check how results are affecting rankings."
                />
                <QuickLink
                  to={`/tournaments/${tournamentId}/announcements`}
                  title="Announcements"
                  description="Publish or review the latest tournament updates."
                />
              </CardContent>
            </Card>
          </div>
        </section>
      </div>
    </Layout>
  );
};

const FixtureLane = ({ title, subtitle, icon: Icon, tone, matches, canEdit }) => {
  const toneClasses = {
    live: 'text-rose-600',
    scheduled: 'text-sky-600',
    completed: 'text-emerald-600',
  };

  return (
    <section className="space-y-3">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <Icon className={cn('h-5 w-5', toneClasses[tone])} />
            <h3 className="text-lg font-semibold tracking-tight">{title}</h3>
          </div>
          <p className="mt-1 text-sm text-muted-foreground">{subtitle}</p>
        </div>
        <Badge variant="outline" className="rounded-full px-3 py-1">
          {matches.length}
        </Badge>
      </div>

      <div className="space-y-3">
        {matches.map((match, index) => (
          <MatchCard key={match.id} match={match} index={index} canEdit={canEdit} />
        ))}
      </div>
    </section>
  );
};

const MatchCard = ({ match, index = 0, canEdit = false }) => {
  const currentInnings = match.innings?.[match.innings.length - 1];

  const statusTone = {
    live: 'border-rose-500/25 bg-rose-500/[0.04]',
    scheduled: 'border-sky-500/20 bg-sky-500/[0.035]',
    completed: 'border-emerald-500/20 bg-emerald-500/[0.035]',
  };

  return (
    <Card
      className={cn(
        'border-white/10 shadow-sm transition duration-200 hover:-translate-y-0.5 hover:shadow-md',
        statusTone[match.status] || 'bg-background'
      )}
      style={{ animationDelay: `${index * 50}ms` }}
      data-testid={`match-card-${match.id}`}
    >
      <CardContent className="p-4 md:p-5">
        <div className="flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between">
          <div className="min-w-0 flex-1 space-y-4">
            <div className="flex flex-wrap items-center gap-2">
              {match.status === 'live' && <Badge className="bg-rose-500 text-white pulse-live">LIVE</Badge>}
              {match.status === 'completed' && <Badge className="bg-emerald-600 text-white">Completed</Badge>}
              {match.status === 'scheduled' && <Badge variant="secondary">Scheduled</Badge>}
              {match.round_name && <Badge variant="outline">{match.round_name}</Badge>}
            </div>

            <div className="grid gap-4 md:grid-cols-[1fr_auto_1fr] md:items-center">
              <div className="rounded-2xl border border-border/70 bg-background/70 p-4">
                <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground">Team 1</p>
                <p className="mt-2 text-lg font-semibold">{match.team1?.name}</p>
              </div>

              <div className="flex items-center justify-center">
                <div className="rounded-full border border-border bg-muted/60 px-4 py-2 text-xs font-semibold uppercase tracking-[0.24em] text-muted-foreground">
                  VS
                </div>
              </div>

              <div className="rounded-2xl border border-border/70 bg-background/70 p-4">
                <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground">Team 2</p>
                <p className="mt-2 text-lg font-semibold">{match.team2?.name}</p>
              </div>
            </div>

            {currentInnings && match.status !== 'scheduled' && (
              <div className="rounded-2xl border border-border/70 bg-background/80 p-4">
                <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                  <div>
                    <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground">
                      Active innings
                    </p>
                    <p className="mt-1 text-sm text-muted-foreground">
                      {currentInnings.batting_team?.name}
                    </p>
                  </div>
                  <div className="text-left md:text-right">
                    <p className="text-3xl font-semibold tracking-tight">
                      {currentInnings.runs}/{currentInnings.wickets}
                    </p>
                    <p className="text-sm text-muted-foreground">
                      {currentInnings.overs}.{currentInnings.balls} overs
                    </p>
                  </div>
                </div>
                {currentInnings.target && (
                  <p className="mt-3 text-sm font-medium text-muted-foreground">
                    Target: {currentInnings.target}
                  </p>
                )}
              </div>
            )}

            {match.result && (
              <div className="rounded-2xl border border-emerald-500/20 bg-emerald-500/[0.06] px-4 py-3 text-sm font-medium text-emerald-700">
                {match.result}
              </div>
            )}

            <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-muted-foreground">
              <span className="inline-flex items-center gap-1.5">
                <Calendar className="h-3.5 w-3.5" />
                {new Date(match.match_date).toLocaleDateString()}
              </span>
              {match.match_time && (
                <span className="inline-flex items-center gap-1.5">
                  <Clock className="h-3.5 w-3.5" />
                  {match.match_time}
                </span>
              )}
              {match.venue && (
                <span className="inline-flex items-center gap-1.5">
                  <MapPin className="h-3.5 w-3.5" />
                  {match.venue}
                </span>
              )}
            </div>
          </div>

          <div className="flex shrink-0 flex-col gap-2 xl:min-w-[170px]">
            {match.status === 'scheduled' && canEdit && (
              <Link to={`/scoring/${match.id}`} className="w-full">
                <Button className="w-full gap-2" data-testid={`start-match-${match.id}`}>
                  <Play className="h-4 w-4" />
                  Start Match
                </Button>
              </Link>
            )}

            {match.status === 'live' && canEdit && (
              <Link to={`/scoring/${match.id}`} className="w-full">
                <Button
                  variant="destructive"
                  className="w-full gap-2"
                  data-testid={`continue-match-${match.id}`}
                >
                  <Play className="h-4 w-4" />
                  Continue Scoring
                </Button>
              </Link>
            )}

            {match.status === 'live' && !canEdit && (
              <Link to={`/live/${match.id}`} className="w-full">
                <Button variant="outline" className="w-full gap-2" data-testid={`watch-match-${match.id}`}>
                  Watch Live
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </Link>
            )}

            {match.status === 'completed' && (
              <Link to={`/live/${match.id}`} className="w-full">
                <Button variant="outline" className="w-full" data-testid={`view-match-${match.id}`}>
                  View Match
                </Button>
              </Link>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const InsightRow = ({ icon: Icon, title, description }) => (
  <div className="flex items-start gap-3">
    <div className="rounded-xl border border-white/10 bg-white/8 p-2">
      <Icon className="h-4 w-4 text-white" />
    </div>
    <div>
      <p className="font-medium text-white">{title}</p>
      <p className="mt-1 text-sm text-white/65">{description}</p>
    </div>
  </div>
);

const QuickLink = ({ to, title, description }) => (
  <Link
    to={to}
    className="flex items-center justify-between rounded-2xl border border-border bg-card px-4 py-3 transition hover:border-primary/30 hover:bg-primary/[0.04]"
  >
    <div>
      <p className="font-medium">{title}</p>
      <p className="mt-1 text-sm text-muted-foreground">{description}</p>
    </div>
    <ChevronRight className="h-4 w-4 text-muted-foreground" />
  </Link>
);

export default Matches;
